import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AlertService } from './alert/alert.service';
import { config } from 'config';
import { createHttpParams } from '../helpers/utils';

export interface ExportRequest {
    type: 'facility' | 'performer';
    format: 'csv' | 'xlsx';
    emailAddresses: string;
    referral_group_id?: string;
    search_term?: string;
    search_type?: string;
    export: 1 | 0;
}

export interface ExportResponse {
    success: boolean;
    jobId: string;
    message: string;
    estimatedCompletionTime?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ExportService {

    constructor(
        private http: HttpClient,
        private alertService: AlertService,
    ) { }

    /**
     * Submit export request for facility data using the same API as loadFacilities
     */
    exportFacilities(request: ExportRequest): Observable<ExportResponse> {
        const baseUrl = `${config.apiUrl1}facility/get`;
        const params = createHttpParams(request);
        return this.http.get<ExportResponse>(baseUrl, { params });
    }

    /**
     * Submit export request for performer data using the same API as loadPerformers
     */
    exportPerformers(request: ExportRequest): Observable<ExportResponse> {
        const baseUrl = `${config.apiUrl1}performer/get`;
        const params = createHttpParams(request);
        return this.http.get<ExportResponse>(baseUrl, { params });
    }

    /**
     * Generic export method that routes to the correct endpoint
     */
    exportData(request: ExportRequest): Observable<ExportResponse> {
        switch (request.type) {
            case 'facility':
                return this.exportFacilities(request);
            case 'performer':
                return this.exportPerformers(request);
            default:
                throw new Error(`Unsupported export type: ${request.type}`);
        }
    }

    /**
     * Execute export with UI feedback handling
     * This method handles the common logic for both facility and performer exports
     */
    executeExportWithFeedback(
        exportRequest: ExportRequest,
        setLoadingCallback: (loading: boolean) => void
    ): void {
        setLoadingCallback(true);

        this.exportData(exportRequest).subscribe({
            next: (response) => {
                setLoadingCallback(false);

                if (response.success) {
                    this.alertService.notifySuccess(
                        `Your ${exportRequest.type} export will be sent to your email shortly.`
                    );
                } else {
                    this.alertService.notifyError(response.message || 'Failed to create export job');
                }
            },
            error: (error) => {
                setLoadingCallback(false);
                if (error.error?.message) {
                    this.alertService.notifyError(`Export failed: ${error.error.message}`);
                } else {
                    this.alertService.notifyError('Failed to create export job. Please try again.');
                }
            }
        });
    }
}
